import { Accordion, Button, Collapse, Group, Stack, Text, Tooltip } from '@mantine/core';
import { IconPlus } from '@tabler/icons-react';
import { useState } from 'react';
import PageLoader from 'src/UI/loader/PageLoader';

import {
  type ArenaRun,
  type ArenaTask,
  type ReportError,
  useArenaCapability,
  useArenaRuns,
} from '../hooks/useModelArena';
import { matchesQuery } from '../runGrouping';
import { dateLabel } from '../runLabels';

import { Detail } from './Detail';
import { readable, sentenceCase } from './metricFormat';
import { NewRunForm } from './NewRunForm';
import { RunHistory } from './RunHistory';

export function TaskAccordionItem({
  task,
  isOpen,
  query,
  reportError,
}: {
  task: ArenaTask;
  isOpen: boolean;
  query: string;
  reportError: ReportError;
}) {
  // Runs are only fetched for a task the reader has actually opened, so polling follows what is on
  // screen rather than every task the arena knows about.
  const { runs, isLoading } = useArenaRuns(isOpen ? task.taskKey : null);

  return (
    <Accordion.Item value={task.taskKey} role="region">
      <Accordion.Control>
        <Text fw={600} size="sm">
          {sentenceCase(task.taskKey)}
        </Text>
      </Accordion.Control>
      <Accordion.Panel>
        {isLoading ? (
          <PageLoader />
        ) : (
          <TaskPanel task={task} runs={runs ?? []} query={query} reportError={reportError} />
        )}
      </Accordion.Panel>
    </Accordion.Item>
  );
}

function TaskPanel({
  task,
  runs,
  query,
  reportError,
}: {
  task: ArenaTask;
  runs: ArenaRun[];
  query: string;
  reportError: ReportError;
}) {
  const [composing, setComposing] = useState(false);
  const { capability } = useArenaCapability();

  const matching = runs.filter(run => matchesQuery(run, query));

  return (
    <Stack gap="sm">
      <Group justify="space-between" align="flex-start">
        <TaskFixtures task={task} />
        <Tooltip
          label={capability?.reason}
          disabled={capability?.mayRun !== false}
          multiline
          w={300}
        >
          <span>
            <Button
              size="compact-xs"
              variant={composing ? 'subtle' : 'light'}
              leftSection={<IconPlus size={12} />}
              onClick={() => setComposing(open => !open)}
              disabled={capability?.mayRun === false}
            >
              New run
            </Button>
          </span>
        </Tooltip>
      </Group>

      <Collapse expanded={composing}>
        <NewRunForm
          taskKey={task.taskKey}
          scoringModels={task.scoringModels}
          versions={task.fixtureVersions}
          onQueued={() => setComposing(false)}
          reportError={reportError}
        />
      </Collapse>

      {matching.length === 0 && (
        <Text size="sm" c="dimmed">
          {runs.length === 0 ? 'No runs yet for this task.' : `No runs match “${query}”.`}
        </Text>
      )}

      <RunHistory
        runs={matching}
        rankMetric={task.rankMetric}
        pinnedVersion={task.fixtures.version}
        reportError={reportError}
      />
    </Stack>
  );
}

/** What the task is pinned to, which a run gets unless it names another version. */
function TaskFixtures({ task }: { task: ArenaTask }) {
  const { fixtures } = task;

  return (
    <Group gap="lg">
      <Detail label="Fixtures" value={fixtures.name} />
      <Detail label="Location" value={fixtures.location} />
      <Detail label="Pinned version" value={fixtures.version} />
      <Detail label="Documents" value={fixtures.documents?.toString() ?? 'unknown'} />
      <Detail
        label="Published"
        value={fixtures.createdAt ? dateLabel(fixtures.createdAt) : 'unknown'}
      />
      <Detail
        label="Ranked by"
        value={task.rankMetric === null ? 'nothing in particular' : readable(task.rankMetric)}
      />
    </Group>
  );
}
